import { useState, useCallback } from 'react';
import { Card, Title, Text, Flex, Button } from '@tremor/react';
import type { QueueStatsResponse, ProcessingTimeStats } from '../types';
import { api } from '../lib/api';

interface QueuePanelProps {
  data: QueueStatsResponse | null;
  onRetryAllFailed: () => void;
  onRetryQueueFailed?: (queueName: string) => void;
  onFlushQueues?: () => void;
  onResetAll?: () => void;
}

const QUEUE_ORDER = ['fetch', 'extract', 'evaluate', 'annotate', 'tag'];
const QUEUE_LABELS: Record<string, string> = {
  fetch: 'FETCH',
  extract: 'EXTRACT',
  evaluate: 'EVALUATE',
  annotate: 'ANNOTATE',
  tag: 'TAG',
};

export function QueuePanel({ data, onRetryAllFailed, onRetryQueueFailed, onFlushQueues, onResetAll }: QueuePanelProps) {
  const [loadingQueue, setLoadingQueue] = useState<string | null>(null);

  const handleTogglePause = useCallback(async (queueName: string, isPaused: boolean) => {
    setLoadingQueue(queueName);
    try {
      if (isPaused) {
        await api.resumeQueue(queueName);
      } else {
        await api.pauseQueue(queueName);
      }
    } catch (error) {
      console.error('Failed to toggle queue pause state:', error);
    } finally {
      setLoadingQueue(null);
    }
  }, []);

  if (!data) {
    return (
      <Card className="bg-gray-900 ring-gray-800">
        <Title className="text-gray-100">Pipeline Queues</Title>
        <div className="mt-4 h-48 flex items-center justify-center">
          <Text className="text-gray-500">Loading...</Text>
        </div>
      </Card>
    );
  }

  const totals = QUEUE_ORDER.reduce(
    (acc, queueName) => {
      const queue = data.queues[queueName];
      if (queue) {
        acc.waiting += queue.waiting;
        acc.active += queue.active;
        acc.completed += queue.completed;
        acc.failed += queue.failed;
      }
      return acc;
    },
    { waiting: 0, active: 0, completed: 0, failed: 0 }
  );

  // Check if any queue is paused
  const anyPaused = QUEUE_ORDER.some(queueName => data.queues[queueName]?.isPaused);

  return (
    <Card className="bg-gray-900 ring-gray-800 h-full">
      <Flex justifyContent="between" alignItems="center">
        <div className="flex items-center gap-2">
          <Title className="text-gray-100">Pipeline Queues</Title>
          {anyPaused && (
            <span className="px-2 py-0.5 text-xs font-medium bg-yellow-900/50 text-yellow-400 rounded">
              PAUSED
            </span>
          )}
        </div>
        <div className="flex gap-2">
          {totals.failed > 0 && (
            <Button
              size="xs"
              color="red"
              variant="secondary"
              onClick={onRetryAllFailed}
            >
              Retry All Failed ({totals.failed})
            </Button>
          )}
          {onFlushQueues && (
            <Button
              size="xs"
              variant="secondary"
              className="bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700 hover:text-gray-300"
              onClick={onFlushQueues}
            >
              Flush...
            </Button>
          )}
          {onResetAll && (
            <Button
              size="xs"
              variant="secondary"
              className="bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700 hover:text-gray-300"
              onClick={onResetAll}
            >
              Reset All...
            </Button>
          )}
        </div>
      </Flex>

      <div className="mt-4 space-y-4">
        {QUEUE_ORDER.map((queueName) => {
          const queue = data.queues[queueName];
          if (!queue) return null;

          return (
            <QueueRow
              key={queueName}
              queueName={queueName}
              name={QUEUE_LABELS[queueName] || queueName}
              waiting={queue.waiting}
              active={queue.active}
              completed={queue.completed}
              failed={queue.failed}
              isPaused={queue.isPaused}
              isLoading={loadingQueue === queueName}
              workerConfig={queue.workerConfig}
              processingTime={queue.processingTime}
              onTogglePause={() => handleTogglePause(queueName, queue.isPaused)}
              onRetryFailed={onRetryQueueFailed ? () => onRetryQueueFailed(queueName) : undefined}
            />
          );
        })}
      </div>

      {/* Totals */}
      <div className="mt-6 pt-4 border-t border-gray-800">
        <Flex justifyContent="start" className="gap-6">
          <StatBadge label="Active" value={totals.active} color="blue" />
          <StatBadge label="Waiting" value={totals.waiting} color="gray" />
          <StatBadge label="Completed" value={totals.completed} color="emerald" />
          <StatBadge label="Failed" value={totals.failed} color="red" />
        </Flex>
      </div>
    </Card>
  );
}

interface QueueRowProps {
  queueName: string;
  name: string;
  waiting: number;
  active: number;
  completed: number;
  failed: number;
  isPaused: boolean;
  isLoading: boolean;
  workerConfig?: {
    concurrency: number;
    clusterMaxPerSecond: number | null;
  };
  processingTime?: ProcessingTimeStats | null;
  onTogglePause: () => void;
  onRetryFailed?: () => void;
}

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function QueueRow({ name, waiting, active, completed, failed, isPaused, isLoading, workerConfig, processingTime, onTogglePause, onRetryFailed }: QueueRowProps) {
  const total = waiting + active + completed + failed;

  // Build tooltip text for active count
  const concurrencyTooltip = workerConfig
    ? `Concurrency: ${workerConfig.concurrency} per pod${workerConfig.clusterMaxPerSecond ? `\nCluster limit: ${workerConfig.clusterMaxPerSecond}/s` : ''}`
    : undefined;

  // Calculate percentages for the stacked bar (always sums to 100% when total > 0)
  const waitingPct = total > 0 ? (waiting / total) * 100 : 0;
  const activePct = total > 0 ? (active / total) * 100 : 0;
  const completedPct = total > 0 ? (completed / total) * 100 : 0;
  const failedPct = total > 0 ? (failed / total) * 100 : 0;

  return (
    <div className="space-y-2">
      <Flex justifyContent="between" alignItems="center">
        <div className="flex items-center gap-2">
          <button
            onClick={onTogglePause}
            disabled={isLoading}
            className={`
              w-6 h-6 flex items-center justify-center rounded
              transition-colors duration-150
              ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-700 cursor-pointer'}
              ${isPaused ? 'text-yellow-400' : 'text-gray-500 hover:text-gray-300'}
            `}
            title={isPaused ? 'Resume queue' : 'Pause queue'}
          >
            {isLoading ? (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : isPaused ? (
              // Play icon
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            ) : (
              // Pause icon
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            )}
          </button>
          <Text className={`font-mono font-medium w-24 ${isPaused ? 'text-yellow-400' : 'text-gray-300'}`}>
            {name}
          </Text>
          {processingTime && (
            <span
              className="text-xs text-gray-500 font-mono cursor-help"
              title={`Processing Time (from last ${processingTime.sampleSize} jobs)\np50: ${formatMs(processingTime.p50)} (median)\np95: ${formatMs(processingTime.p95)} (95th percentile)\nAvg: ${formatMs(processingTime.avg)}\nMin: ${formatMs(processingTime.min)}\nMax: ${formatMs(processingTime.max)}`}
            >
              <span className="text-gray-400">{formatMs(processingTime.p50)}</span>
              <span className="text-gray-600">/</span>
              <span className="text-gray-500">{formatMs(processingTime.p95)}</span>
            </span>
          )}
          {isPaused && (
            <span className="text-xs text-yellow-500 font-medium">PAUSED</span>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs">
          <span className="text-gray-500">
            <span className="text-gray-400">{waiting}</span> waiting
          </span>
          <span
            className="text-blue-400 cursor-help"
            title={concurrencyTooltip}
          >
            <span className="font-medium">{active}</span> active
            {workerConfig && (
              <span className="text-gray-500 ml-1">/{workerConfig.concurrency}</span>
            )}
          </span>
          <span className="text-emerald-400">
            <span className="font-medium">{completed.toLocaleString()}</span> done
          </span>
          {failed > 0 && (
            onRetryFailed ? (
              <button
                onClick={onRetryFailed}
                className="text-red-400 hover:text-red-300 cursor-pointer"
                title={`Retry ${failed} failed job${failed !== 1 ? 's' : ''}`}
              >
                <span className="font-medium">{failed}</span> <span className="hover:underline">fail</span>
              </button>
            ) : (
              <span className="text-red-400">
                <span className="font-medium">{failed}</span> fail
              </span>
            )
          )}
        </div>
      </Flex>

      {/* Stacked progress bar */}
      <div className={`h-3 bg-gray-800 rounded-full overflow-hidden flex ${isPaused ? 'opacity-50' : ''}`}>
        {completedPct > 0 && (
          <div
            className="bg-emerald-600 h-full transition-all duration-300"
            style={{ width: `${completedPct}%` }}
          />
        )}
        {activePct > 0 && (
          <div
            className="bg-blue-500 h-full transition-all duration-300"
            style={{ width: `${activePct}%` }}
          />
        )}
        {waitingPct > 0 && (
          <div
            className="bg-gray-600 h-full transition-all duration-300"
            style={{ width: `${waitingPct}%` }}
          />
        )}
        {failedPct > 0 && (
          <div
            className="bg-red-500 h-full transition-all duration-300"
            style={{ width: `${failedPct}%` }}
          />
        )}
      </div>
    </div>
  );
}

interface StatBadgeProps {
  label: string;
  value: number;
  color: 'blue' | 'gray' | 'emerald' | 'red';
}

function StatBadge({ label, value, color }: StatBadgeProps) {
  const colorClasses = {
    blue: 'text-blue-400',
    gray: 'text-gray-400',
    emerald: 'text-emerald-400',
    red: 'text-red-400',
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-gray-500 text-sm">{label}:</span>
      <span className={`font-semibold ${colorClasses[color]}`}>
        {value.toLocaleString()}
      </span>
    </div>
  );
}
